import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '@/components/Icon';

interface StepProgressProps {
  n: number;
  total?: number;
  style?: CSSProperties;
}

// Segmented progress bar shared by StepHead (working steps) and SetupDone
// (which uses a larger headline so it can't reuse StepHead wholesale).
export function StepProgress({ n, total = 3, style }: StepProgressProps) {
  return (
    <div style={{ display: 'flex', gap: 6, ...style }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            background: i < n ? 'var(--moss)' : 'var(--paper-3)',
          }}
        />
      ))}
    </div>
  );
}

interface StepHeadProps {
  n: number;
  total?: number;
  eyebrow: string;
  title: ReactNode;
  sub?: string;
}

export function StepHead({ n, total = 3, eyebrow, title, sub }: StepHeadProps) {
  return (
    <div style={{ marginBottom: 22 }}>
      <StepProgress n={n} total={total} style={{ marginBottom: 18 }} />
      <div className="eyebrow" style={{ marginBottom: 10 }}>
        {String(n).padStart(2, '0')} / {String(total).padStart(2, '0')} · {eyebrow}
      </div>
      <h1
        style={{
          fontFamily: "'Instrument Serif', serif",
          fontWeight: 400,
          fontSize: 38,
          lineHeight: 0.98,
          margin: 0,
          color: 'var(--ink)',
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </h1>
      {sub && (
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 12, lineHeight: 1.5 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

interface ImportantNoteProps {
  icon?: IconName;
  children: ReactNode;
}

export function ImportantNote({ icon = 'info', children }: ImportantNoteProps) {
  return (
    <div
      style={{
        background: '#fbf8f1',
        border: '1px solid var(--paper-3)',
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        gap: 12,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          flexShrink: 0,
          background: 'var(--moss-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={14} stroke="var(--moss-deep)" sw={1.8} />
      </div>
      <div style={{ flex: 1, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
        {children}
      </div>
    </div>
  );
}

interface StepHeroProps {
  glyph: string;
  label: string;
  accent?: boolean;
}

// Editorial step illustration — large italic serif glyph on faint horizontal
// rules. Replaces the stock "concentric circles + icon" scene used by other
// VPN apps with our paper-and-ink vocabulary.
export function StepHero({ glyph, label, accent = false }: StepHeroProps) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: 240,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(to bottom, var(--paper-3) 1px, transparent 1px)',
          backgroundSize: '100% 36px',
          opacity: 0.35,
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
        }}
      />
      <div style={{ position: 'relative', textAlign: 'center' }}>
        <div
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: 'italic',
            fontSize: 188,
            lineHeight: 0.85,
            color: accent ? 'var(--moss-deep)' : 'var(--ink)',
            letterSpacing: '-0.04em',
          }}
        >
          {glyph}
        </div>
        <div
          style={{
            marginTop: 12,
            fontSize: 12.5,
            color: 'var(--ink-3)',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

interface SetupSheetButton {
  label: string;
  onClick: () => void;
}

interface SetupSheetProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
  primary?: SetupSheetButton;
  secondary?: SetupSheetButton;
}

// Bottom sheet with paper backdrop. Slides up from bottom on open; backdrop
// fades simultaneously. Tapping outside closes.
export function SetupSheet({ title, children, onClose, primary, secondary }: SetupSheetProps) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(20,19,15,0.4)',
          animation: 'shushFade 240ms ease-out',
        }}
      />
      <div
        style={{
          position: 'relative',
          background: 'var(--paper)',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: '20px 22px 22px',
          boxShadow: '0 -8px 30px rgba(20,19,15,0.18)',
          animation: 'shushSheetUp 280ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--paper-3)',
            margin: '0 auto 16px',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 12,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: "'Instrument Serif', serif",
              fontWeight: 400,
              fontSize: 28,
              lineHeight: 1,
              color: 'var(--ink)',
              letterSpacing: '-0.02em',
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="закрыть"
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: 'var(--paper-2)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="close" size={14} stroke="var(--ink)" sw={2} />
          </button>
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 18 }}>
          {children}
        </div>
        {primary && (
          <button
            onClick={primary.onClick}
            className="btn btn-moss"
            style={{
              width: '100%',
              height: 50,
              fontSize: 14,
              marginBottom: secondary ? 8 : 0,
            }}
          >
            {primary.label}
          </button>
        )}
        {secondary && (
          <button
            onClick={secondary.onClick}
            className="btn btn-paper"
            style={{ width: '100%', height: 46, fontSize: 13 }}
          >
            {secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}

interface ItalicTitleProps {
  children: ReactNode;
}

// Italic emphasis word in moss-deep, used inside serif headlines.
export function Italic({ children }: ItalicTitleProps) {
  return (
    <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>{children}</span>
  );
}
